import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdSelector from "./AdSelector";
import { API_BASE_URL } from "../../config";
import "../../styles/Bookings.css";

// The advertiser's bookings, and the one place a running campaign's artwork
// can be swapped.
//
// Changing the ad is deliberately not "cancel and rebook": that would release
// the screen-days (so someone else could take them), refund one charge and
// make another, and put two more rows through the ledger — for a change that
// costs nothing. It is a request against the existing booking, and the
// broadcaster has to approve it before it reaches the screen, because they
// approved a specific image for a specific window.

const CHANGEABLE = ["pending", "approved"];

// Money the customer is owed, or a dispute. These are not ordinary states and
// should not look like one.
const OWES = ["refund_pending", "refund_failed", "refund_manual", "disputed"];

function money(value) {
    if (value === null || value === undefined || value === "") return "—";
    const n = Number(value);
    return Number.isNaN(n) ? "—" : `$${n.toFixed(2)}`;
}

function when(value) {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function readable(value) {
    return value ? String(value).replace(/_/g, " ") : "—";
}

function statusTone(status) {
    if (status === "approved") return "wb-badge--ok";
    if (status === "pending") return "wb-badge--warn";
    if (["rejected", "denied", "cancelled"].includes(status)) return "wb-badge--danger";
    return "";
}

export default function CustomerDeviceRequests() {
    const [requests, setRequests] = useState([]);
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [openId, setOpenId] = useState(null);
    const [chosenAdId, setChosenAdId] = useState("");
    const [busyId, setBusyId] = useState(null);

    const token = localStorage.getItem("token");
    const authHeaders = { Authorization: `Bearer ${token}` };

    const fetchRequests = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer/requests`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            setRequests(data.requests || []);
        } catch {
            setError("Could not load your bookings.");
        }
        setLoading(false);
    }, [token]);

    useEffect(() => { fetchRequests(); }, [fetchRequests]);

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/customer/ads`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => res.json())
            .then((data) => setAds(data.images || []))
            .catch(() => setAds([]));
    }, [token]);

    async function handleCancel(requestId) {
        if (!window.confirm("Cancel this booking? The screen-days are released.")) return;
        setBusyId(requestId);
        setError(""); setNotice("");
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/customer/requests/${requestId}/cancel`,
                { method: "POST", headers: authHeaders }
            );
            const data = await res.json().catch(() => ({}));
            // The server explains what happened to the money -- refunded,
            // pending, or parked for a human. Discarding that and printing
            // "Request cancelled" is how a customer stops knowing whether they
            // have been paid back.
            if (res.ok) setNotice([data.message, data.refund].filter(Boolean).join(" "));
            else setError(data.message || "Could not cancel this booking.");
        } catch {
            setError("Network error. Nothing changed.");
        }
        setBusyId(null);
        fetchRequests();
    }

    async function submitChange(requestId) {
        if (!chosenAdId) { setError("Choose an ad to switch to."); return; }
        setBusyId(requestId);
        setError(""); setNotice("");
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/customer/requests/${requestId}/creative`,
                {
                    method: "POST",
                    headers: { ...authHeaders, "Content-Type": "application/json" },
                    body: JSON.stringify({ image_id: chosenAdId }),
                }
            );
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setNotice(data.message || "Change requested.");
                setOpenId(null);
                setChosenAdId("");
            } else {
                setError(data.message || "Could not request the change.");
            }
        } catch {
            setError("Network error. Nothing changed.");
        }
        setBusyId(null);
        fetchRequests();
    }

    async function withdrawChange(requestId) {
        setBusyId(requestId);
        setError(""); setNotice("");
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/customer/requests/${requestId}/creative`,
                { method: "DELETE", headers: authHeaders }
            );
            const data = await res.json().catch(() => ({}));
            if (res.ok) setNotice(data.message || "Change withdrawn.");
            else setError(data.message || "Could not withdraw the change.");
        } catch {
            setError("Network error. Nothing changed.");
        }
        setBusyId(null);
        fetchRequests();
    }

    if (loading) return <div className="bg"><p className="wb-sub">Loading your bookings…</p></div>;

    return (
        <div className="bg">
            <header className="bg__head">
                <h1>Your bookings</h1>
                <p>
                    You can change the ad on a booking that has not finished. The screen
                    keeps playing the approved ad until the host reviews the new
                    one, and there is no extra charge.
                </p>
            </header>

            {error && <div className="wb-note wb-note--danger">{error}</div>}
            {notice && !error && <div className="wb-note wb-note--ok">{notice}</div>}

            {requests.length === 0 ? (
                <div className="wb-empty">
                    You have not booked anything yet.{" "}
                    <Link to="/devices">Browse screens</Link>
                </div>
            ) : (
                <div className="bg__list">
                    {requests.map((req) => {
                        const pending = req.pending_creative_change;
                        const canChange = CHANGEABLE.includes(req.status)
                            && new Date(req.end_time) > new Date();
                        const busy = busyId === req.id;

                        return (
                            <article
                                className={"bg__card" + (pending ? " is-awaiting" : "")}
                                key={req.id}
                            >
                                <div className="bg__row">
                                    <div>
                                        <h2 className="bg__screen">
                                            {req.device_name || `Screen #${req.device_id}`}
                                        </h2>
                                        <p className="bg__dates">
                                            {when(req.start_time)} – {when(req.end_time)}
                                        </p>
                                    </div>
                                    <div className="bg__money">
                                        <span className="bg__total">{money(req.amount)}</span>
                                        <span className="bg__unit">
                                            {money(req.proposed_rate)}/day
                                        </span>
                                    </div>
                                </div>

                                <div className="bg__badges">
                                    <span className={"wb-badge " + statusTone(req.status)}>
                                        {readable(req.status)}
                                    </span>
                                    <span className={
                                        "wb-badge " +
                                        (OWES.includes(req.payment_status) ? "wb-badge--danger" : "")
                                    }>
                                        {readable(req.payment_status)}
                                    </span>
                                </div>

                                {req.rejection_reason && (
                                    <div className="wb-note wb-note--danger">
                                        {req.rejection_reason}
                                        {req.vendor_note ? ` — ${req.vendor_note}` : ""}
                                    </div>
                                )}

                                {pending && (
                                    <div className="bg__pending">
                                        <div className="bg__pending-text">
                                            <strong>New ad waiting for the host</strong>
                                            <span>
                                                Requested {when(pending.requested_at)}. The
                                                approved ad keeps running until they answer.
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className="wb-btn wb-btn--secondary wb-btn--sm"
                                            onClick={() => withdrawChange(req.id)}
                                            disabled={busy}
                                        >
                                            Withdraw
                                        </button>
                                    </div>
                                )}

                                <div className="bg__actions">
                                    {canChange && !pending && (
                                        <button
                                            type="button"
                                            className="wb-btn wb-btn--secondary"
                                            onClick={() => {
                                                setOpenId(openId === req.id ? null : req.id);
                                                setChosenAdId("");
                                            }}
                                        >
                                            {openId === req.id ? "Cancel" : "Change the ad"}
                                        </button>
                                    )}
                                    {["pending", "approved"].includes(req.status) && (
                                        <button
                                            type="button"
                                            className="wb-btn wb-btn--danger"
                                            onClick={() => handleCancel(req.id)}
                                            disabled={busy}
                                        >
                                            Cancel booking
                                        </button>
                                    )}
                                </div>

                                {openId === req.id && (
                                    <div className="bg__picker">
                                        <h3>Choose the new ad</h3>
                                        <p className="bg__picker-note">
                                            {req.status === "approved"
                                                ? "The host approved the current ad for their business, so they review this one before it plays. Your dates, price and payment do not change."
                                                : "This booking has not been reviewed yet, so the new ad simply replaces the old one — the host will see it when they review the booking."}
                                        </p>
                                        <AdSelector
                                            ads={ads}
                                            selectedAdId={chosenAdId}
                                            onSelect={setChosenAdId}
                                        />
                                        <div>
                                            <button
                                                type="button"
                                                className="wb-btn wb-btn--primary"
                                                onClick={() => submitChange(req.id)}
                                                disabled={busy || !chosenAdId}
                                            >
                                                {busy ? "Sending…" : "Request this ad"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
